import { useContext } from "react";
import {
    CounterpartyDetailChanges,
    hasCounterpartyDetailChanges,
} from "@common/helpers/counterpartyDetailChanges";
import {
    CounterpartyReplacePreview,
    counterpartyDetailWritesForRecord,
    counterpartyReplacePreview,
} from "@common/helpers/counterpartyDetailChangeWrites";
import { CreateCounterpartyAddressBody } from "@common/schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "@common/schemas/counterpartyContact.schema";
import { Counterparty } from "@common/types/counterparty";
import {
    FinancialDocumentExtended,
    ImporterErrors,
} from "@common/types/financialDocument";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useRemoveImporterErrorMutation } from "@api/financialDocument.api";
import { AccrualImporterContext } from "../accrualImporter.context";
import { AccrualDocumentImporterMode } from "../accrualDocumentImporter.lib";
import { useAccrualCounterpartyDetailDiff } from "../useAccrualCounterpartyDetailDiff";
import { accrualDraftCopy } from "./accrualDraftCopy";

export interface AccrualCounterpartyDetailChangesState {
    changes: CounterpartyDetailChanges;
    counterparty: Counterparty | null;
    mergeSeeds: {
        contacts: CreateCounterpartyContactBody[];
        addresses: CreateCounterpartyAddressBody[];
    };
    replacePreview: CounterpartyReplacePreview;
    shouldBlockConfirm: boolean;
    isDetailsError: boolean;
    retryDetails: () => void;
    finishMerge: (counterparty: Counterparty) => void;
    skip: () => void;
}

export function useAccrualCounterpartyDetailChanges({
    financialDocument,
    extractionId,
    counterpartyId,
    mode,
    onCounterpartyLocalized,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId: string | null;
    counterpartyId: string | null;
    mode: AccrualDocumentImporterMode;
    onCounterpartyLocalized: (counterparty: Counterparty) => Promise<void>;
}): AccrualCounterpartyDetailChangesState {
    const { revalidateDocument } = useContext(AccrualImporterContext);
    const toaster = useToaster();
    const copy = accrualDraftCopy(mode);
    const { changes, extracted, details, isLoading, isError, refetch } =
        useAccrualCounterpartyDetailDiff({
            financialDocument,
            extractionId,
            counterpartyId,
            mode,
        });
    const removeImporterError =
        useRemoveImporterErrorMutation(financialDocument);

    const mergeWrites =
        details && extracted
            ? counterpartyDetailWritesForRecord({
                  action: "merge",
                  extracted,
                  record: details,
              })
            : null;
    const replacePreview: CounterpartyReplacePreview =
        details && extracted
            ? counterpartyReplacePreview({
                  writes: counterpartyDetailWritesForRecord({
                      action: "replace",
                      extracted,
                      record: details,
                  }),
                  contacts: details.contacts,
                  addresses: details.addresses,
              })
            : {
                  removedContactIds: [],
                  removedAddressIds: [],
                  contacts: [],
                  addresses: [],
              };

    const finishMerge = (counterparty: Counterparty) => {
        if (counterparty.id !== details?.counterparty.id) {
            void onCounterpartyLocalized(counterparty);
        }
        revalidateDocument({ financialDocumentId: financialDocument.id });
    };

    const skip = () => {
        removeImporterError.mutate(
            ImporterErrors.ACCRUAL_COUNTERPARTY_DETAILS_CHANGED,
            {
                onError: () => {
                    toaster.error(copy.skipChangedDetailsError);
                },
            },
        );
    };

    return {
        changes,
        counterparty: details?.counterparty ?? null,
        mergeSeeds: {
            contacts: mergeWrites?.contactCreates ?? [],
            addresses: mergeWrites?.addressCreates ?? [],
        },
        replacePreview,
        shouldBlockConfirm:
            isLoading || isError || hasCounterpartyDetailChanges(changes),
        isDetailsError: isError,
        retryDetails: refetch,
        finishMerge,
        skip,
    };
}
